'use client';

import { useEffect, useRef, useState } from 'react';

interface UseProactiveGreetingParams {
  /** Thread the chat runs on. Null until the NextAuth session status resolves. */
  threadId: string | null;
  /** Produces Bestie's opening turn. What that means — a skill invoke for signed-in visitors,
   *  a hidden priming turn for guests — is the caller's decision. */
  startOpener: () => Promise<void>;
}

/**
 * Makes Bestie open the conversation on the chat page.
 *
 * This hook owns only *when* the opener fires: once per mount, as soon as a thread id exists.
 * The ref guard absorbs React's StrictMode double-mount and the re-render when `startOpener`'s
 * identity changes as the session resolves, while a real remount counts as a new entry.
 *
 * `isGreetingPending` stays false while `threadId` is null, then covers the opener until it
 * settles either way. Failures are silent: the caller falls back to the canned landing and the
 * prompt bar still works. One attempt per mount, no retry.
 */
export const useProactiveGreeting = ({
  threadId,
  startOpener,
}: UseProactiveGreetingParams): { isGreetingPending: boolean } => {
  const greetedThreadRef = useRef<string | null>(null);
  const [isGreetingPending, setIsGreetingPending] = useState(false);

  useEffect(() => {
    if (!threadId) return;
    if (greetedThreadRef.current === threadId) return;
    greetedThreadRef.current = threadId;

    setIsGreetingPending(true);
    void startOpener()
      // Silent to the *user* — the canned landing renders and the prompt bar still works — but
      // not to the console: without this, a broken `chat/invoke` (missing `force_skills` support,
      // no `home-greeting` skill in the deployment) is indistinguishable from "no greeting
      // configured", which is exactly the ambiguity the spec's release note warns about.
      .catch((error: unknown) => console.error('useProactiveGreeting: opener failed', error))
      .finally(() => setIsGreetingPending(false));
  }, [threadId, startOpener]);

  return { isGreetingPending };
};
